import { Box, Grid, useMediaQuery } from '@mui/material'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { ReactNode } from 'react'

export interface PageContainerProps {
  /** Content to render inside the container */
  children: ReactNode
  /** Loading state - shows loadingComponent when true */
  isLoading?: boolean
  /** Component to render when loading */
  loadingComponent?: ReactNode
  /** Whether to use auto overflow (default: hidden) */
  autoOverflow?: boolean
  /** Custom height calculation (default: 'calc(100vh - 120px)') */
  height?: string
  /** Padding value (default: 0) */
  padding?: number | string
  /** Custom styles */
  sx?: object
}

/** Height offset when navigation is pinned (visible) */
const NAV_PINNED_OFFSET = 55

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  isLoading = false,
  loadingComponent,
  autoOverflow = false,
  height,
  padding = 0,
  sx,
}) => {
  const { pinned } = useLayoutContext()
  // MacBook Pro specific media query (matches existing pattern)
  const isMacbookPro = useMediaQuery(
    '(min-width: 1512px) and (max-width: 1512px) and (min-height: 857px) and (max-height: 857px)'
  )

  // Calculate base height offset
  const baseOffset = isMacbookPro ? 115 : 120
  // Add nav offset when pinned
  const totalOffset = pinned ? baseOffset + NAV_PINNED_OFFSET : baseOffset

  // Use custom height if provided, adjusting for pinned state
  const containerHeight = height
    ? pinned
      ? `calc(${height} - ${NAV_PINNED_OFFSET}px)`
      : height
    : `calc(100vh - ${totalOffset}px)`

  if (isLoading && loadingComponent) {
    return (
      <Grid
        container
        sx={{
          height: containerHeight,
          transition: 'height 0.3s ease',
          overflowY: 'hidden',
          overflowX: 'auto',
          p: padding,
          ...sx,
        }}
      >
        {loadingComponent}
      </Grid>
    )
  }

  return (
    <Grid
      container
      sx={{
        height: containerHeight,
        transition: 'height 0.3s ease',
        overflowY: autoOverflow ? 'auto' : 'hidden',
        overflowX: 'auto',
        p: padding,
        ...sx,
      }}
    >
      <Grid item xs={12} sx={{ height: '100%' }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</Box>
      </Grid>
    </Grid>
  )
}

export default PageContainer
